import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import {
  useCompleteInstructionMutation,
  useLazyDownloadInstructionDocxQuery,
  useListMyInstructionsQuery,
} from '@shared/api';
import { apiErrorMessage, saveBlob } from '@entities/progress';
import { InstructionCard } from '@pages/Progress/instructions/InstructionCard';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

/* «Мои поручения» — поручения руководителя, выданные сотруднику по
   показателям объекта (ОСНОВА.Руководитель). Доступно любому сотруднику:
   прав на агента не нужно, видны только свои поручения. */

const Page = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const H1 = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const Lead = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Empty = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(6)} 0`};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const NoteRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex: 1;
  min-width: 260px;
`;

/** Роут `/instructions` — мои поручения (`?id=` — подсветить и раскрыть). */
export function MyInstructionsPage() {
  const [params] = useSearchParams();
  const focusId = params.get('id');
  const listQ = useListMyInstructionsQuery();
  const [download] = useLazyDownloadInstructionDocxQuery();
  const [complete, { isLoading: completing }] = useCompleteInstructionMutation();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const items = listQ.data ?? [];

  useEffect(() => {
    if (!focusId || !listQ.data) return;
    document.getElementById(`instruction-${focusId}`)?.scrollIntoView({ block: 'center' });
  }, [focusId, listQ.data]);

  async function handleDownload(id: string, number: string) {
    setError(null);
    try {
      const blob = await download(id).unwrap();
      saveBlob(blob, `Поручение_${number}.docx`);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось скачать документ.');
    }
  }

  async function handleDone(id: string) {
    setError(null);
    try {
      await complete({ id, note: notes[id]?.trim() || null }).unwrap();
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось отметить выполнение.');
    }
  }

  return (
    <Page>
      <div>
        <H1>Мои поручения</H1>
        <Lead>
          Поручения руководителя по показателям объектов. Отметьте выполнение здесь или кнопкой
          «Выполнено» в сообщении VK WorkSpace — руководитель увидит отметку сразу.
        </Lead>
      </div>
      {error && <Err role="alert">{error}</Err>}
      {listQ.isLoading ? (
        <Empty>Загрузка…</Empty>
      ) : items.length === 0 ? (
        <Empty>Поручений нет.</Empty>
      ) : (
        items.map((it) => (
          <InstructionCard
            key={it.id}
            item={it}
            perspective="assignee"
            highlight={it.id === focusId}
            actions={
              <>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  onClick={() => handleDownload(it.id, it.number)}
                >
                  Документ (DOCX)
                </Button>
                {it.status === 'issued' && (
                  <NoteRow>
                    <Input
                      value={notes[it.id] ?? ''}
                      onChange={(e) => setNotes((n) => ({ ...n, [it.id]: e.target.value }))}
                      placeholder="Отметка: что сделано (необязательно)"
                      maxLength={1000}
                      aria-label="Отметка о выполнении"
                    />
                    <Button
                      type="button"
                      $variant="primary"
                      $size="sm"
                      disabled={completing}
                      onClick={() => handleDone(it.id)}
                    >
                      Выполнено
                    </Button>
                  </NoteRow>
                )}
              </>
            }
          />
        ))
      )}
    </Page>
  );
}
